/**
 * @fileoverview Página para exportar informe de operarios/proveedores a Excel
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Button,
  Alert,
  Spinner,
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { RepositoryFactory } from '../../repositories';
import { validateDateRange, hasActiveFilters } from '../../helpers/informeOperariosValidations';
import { generateExcel } from '../../helpers/informeOperariosExcelHelper';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';

const InformeOperariosPage = () => {
  document.title = 'Informe Operarios | GraphicForce - Printesco';

  // Estados de filtros
  const [filters, setFilters] = useState({
    operario: null,
    proveedor: null,
    orden: null,
    centroCosto: null,
    fechaInicio: { inicio: null, fin: null },
    fechaFin: { inicio: null, fin: null },
  });

  // Estados de validación
  const [dateErrors, setDateErrors] = useState({
    fechaInicio: null,
    fechaFin: null,
  });

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [operarios, setOperarios] = useState([]);
  const [loadingOperarios, setLoadingOperarios] = useState(true);
  const [proveedores, setProveedores] = useState([]);
  const [loadingProveedores, setLoadingProveedores] = useState(true);
  const [ordenes, setOrdenes] = useState([]);
  const [loadingOrdenes, setLoadingOrdenes] = useState(true);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [loadingCentrosCosto, setLoadingCentrosCosto] = useState(true);

  // Repositorios
  const logRepository = useMemo(() => RepositoryFactory.getRepository('log_plan_centro_costo'), []);
  const operarioRepository = useMemo(() => RepositoryFactory.getRepository('users'), []);
  const proveedorRepository = useMemo(() => RepositoryFactory.getRepository('proceso_proveedor'), []);
  const ordenRepository = useMemo(() => RepositoryFactory.getRepository('orden_produccion'), []);
  const centroCostoRepository = useMemo(() => RepositoryFactory.getRepository('centro_costo'), []);

  // Cargar operarios al montar
  useEffect(() => {
    const loadOperarios = async () => {
      try {
        setLoadingOperarios(true);
        const operariosData = await operarioRepository.getOperariosParaAutocompletado();
        console.log('Operarios cargados:', operariosData); // Debug log
        setOperarios(operariosData.map(op => {
          const nombre = op.name || op.email || 'Sin nombre';
          const estaActivo = op.activo !== false; // Default to true if undefined
          const label = estaActivo ? nombre : `${nombre} (desactivado)`;
          return { 
            value: op.id, 
            label: label
          };
        }));
        console.log('Operarios mapeados:', operariosData.map(op => {
          const nombre = op.name || op.email || 'Sin nombre';
          const estaActivo = op.activo !== false;
          return { 
            value: op.id, 
            label: estaActivo ? nombre : `${nombre} (desactivado)`
          };
        })); // Debug log
      } catch (err) {
        console.error('Error loading operarios:', err);
        setError('Error al cargar operarios');
      } finally {
        setLoadingOperarios(false);
      }
    };

    loadOperarios();
  }, [operarioRepository]);

  // Cargar proveedores al montar
  useEffect(() => {
    const loadProveedores = async () => {
      try {
        setLoadingProveedores(true);
        const proveedoresData = await proveedorRepository.getAllActive();
        console.log('Proveedores cargados:', proveedoresData); // Debug log
        setProveedores(proveedoresData.map(p => ({ 
          value: p.id, 
          label: p.nombre || 'Sin nombre' 
        })));
        console.log('Proveedores mapeados:', proveedoresData.map(p => ({ 
          value: p.id, 
          label: p.nombre || 'Sin nombre' 
        }))); // Debug log
      } catch (err) {
        console.error('Error loading proveedores:', err);
        setError('Error al cargar proveedores');
      } finally {
        setLoadingProveedores(false);
      }
    };

    loadProveedores();
  }, [proveedorRepository]);

  // Cargar órdenes al montar
  useEffect(() => {
    const loadOrdenes = async () => {
      try {
        setLoadingOrdenes(true);
        const ordenesData = await ordenRepository.getOrdenesParaAutocompletado();
        setOrdenes(ordenesData.map(o => ({ 
          value: o.id, 
          label: `Orden ${o.numero}` 
        })));
      } catch (err) {
        console.error('Error loading ordenes:', err);
        setError('Error al cargar órdenes');
      } finally {
        setLoadingOrdenes(false);
      }
    };

    loadOrdenes();
  }, [ordenRepository]);

  // Cargar centros de costo al montar
  useEffect(() => {
    const loadCentrosCosto = async () => {
      try {
        setLoadingCentrosCosto(true);
        const centrosCostoData = await centroCostoRepository.getAllActive();
        setCentrosCosto(centrosCostoData.map(cc => ({ 
          value: cc.id, 
          label: cc.nombre || 'Sin nombre' 
        })));
      } catch (err) {
        console.error('Error loading centros de costo:', err);
        setError('Error al cargar centros de costo');
      } finally {
        setLoadingCentrosCosto(false);
      }
    };

    loadCentrosCosto();
  }, [centroCostoRepository]);

  // Validar filtros activos
  const hasFilters = useMemo(() => hasActiveFilters(filters), [filters]);

  // Handler genérico para fechas
  const handleDateChange = (dateType, rangeType, date) => {
    const dateValue = date ? (Array.isArray(date) ? date[0] : date) : null;
    const dateString = dateValue ? new Date(dateValue).toISOString().split('T')[0] : null;

    setFilters(prev => {
      const newFilters = {
        ...prev,
        [dateType]: {
          ...prev[dateType],
          [rangeType]: dateString,
        },
      };

      // Validar rango de fechas (máximo 60 días)
      if (dateType === 'fechaInicio' || dateType === 'fechaFin') {
        const inicio = newFilters[dateType].inicio;
        const fin = newFilters[dateType].fin;

        if (inicio && fin) {
          const error = validateDateRange(inicio, fin, 60);
          setDateErrors(prev => ({
            ...prev,
            [dateType]: error,
          }));
        } else {
          setDateErrors(prev => ({
            ...prev,
            [dateType]: null,
          }));
        }
      }

      return newFilters;
    });

    setError(null);
  };

  // Handler para operario
  const handleOperarioChange = (selectedOption) => {
    setFilters(prev => ({
      ...prev,
      operario: selectedOption ? selectedOption.value : null,
    }));
    setError(null);
  };

  // Handler para proveedor
  const handleProveedorChange = (selectedOption) => {
    setFilters(prev => ({
      ...prev,
      proveedor: selectedOption ? selectedOption.value : null,
    }));
    setError(null);
  };

  // Handler para orden
  const handleOrdenChange = (selectedOption) => {
    setFilters(prev => ({
      ...prev,
      orden: selectedOption ? selectedOption.value : null,
    }));
    setError(null);
  };

  // Handler para centro de costo
  const handleCentroCostoChange = (selectedOption) => {
    setFilters(prev => ({
      ...prev,
      centroCosto: selectedOption ? selectedOption.value : null,
    }));
    setError(null);
  };

  // Handler para exportar
  const handleExport = async () => {
    // Validar que no haya errores de fecha
    const hasDateErrors = Object.values(dateErrors).some(err => err !== null);
    if (hasDateErrors) {
      setError('Por favor, corrija los errores en los rangos de fechas antes de exportar.');
      return;
    }

    // Validar que haya al menos un filtro activo
    if (!hasFilters) {
      setError('Por favor, seleccione al menos un filtro antes de exportar.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Preparar filtros para el repositorio
      const reporteFilters = {
        operario: filters.operario,
        proveedor: filters.proveedor,
        orden: filters.orden,
        centroCosto: filters.centroCosto,
        fechaInicio: filters.fechaInicio,
        fechaFin: filters.fechaFin,
      };

      // Obtener logs
      const logs = await logRepository.getLogsParaReporte(reporteFilters);

      if (logs.length === 0) {
        setError('No se encontraron registros con los filtros seleccionados.');
        setLoading(false);
        return;
      }

      // Generar Excel
      generateExcel(logs);
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.message || 'Error al generar el reporte. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs maintitle="Dashboard" title="Exportación" breadcrumbItem="Informe Operarios" />

        <Row>
          <Col lg="12">
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Informe de Operarios</h4>
              </CardHeader>
              <CardBody>
                {error && (
                  <Alert color="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {!hasFilters && (
                  <Alert color="info" className="mb-3">
                    Por favor, seleccione al menos un filtro para continuar.
                  </Alert>
                )}

                <Form>
                  <Row>
                    {/* Operario */}
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="operario">Operario</Label>
                        <Select
                          id="operario"
                          options={operarios}
                          isClearable
                          isSearchable
                          placeholder={loadingOperarios ? 'Cargando...' : 'Seleccionar operario'}
                          isLoading={loadingOperarios}
                          value={operarios.find(o => o.value === filters.operario) || null}
                          onChange={handleOperarioChange}
                        />
                      </FormGroup>
                    </Col>

                    {/* Proveedor */}
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="proveedor">Proveedor</Label>
                        <Select
                          id="proveedor"
                          options={proveedores}
                          isClearable
                          isSearchable
                          placeholder={loadingProveedores ? 'Cargando...' : 'Seleccionar proveedor'}
                          isLoading={loadingProveedores}
                          value={proveedores.find(p => p.value === filters.proveedor) || null}
                          onChange={handleProveedorChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    {/* Orden de Producción */}
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="orden">Orden de Producción</Label>
                        <Select
                          id="orden"
                          options={ordenes}
                          isClearable
                          isSearchable
                          placeholder={loadingOrdenes ? 'Cargando...' : 'Seleccionar orden'}
                          isLoading={loadingOrdenes}
                          value={ordenes.find(o => o.value === filters.orden) || null}
                          onChange={handleOrdenChange}
                        />
                      </FormGroup>
                    </Col>

                    {/* Centro de Costo */}
                    <Col md="6">
                      <FormGroup>
                        <Label htmlFor="centro-costo">Centro de Costo</Label>
                        <Select
                          id="centro-costo"
                          options={centrosCosto}
                          isClearable
                          isSearchable
                          placeholder={loadingCentrosCosto ? 'Cargando...' : 'Seleccionar centro de costo'}
                          isLoading={loadingCentrosCosto}
                          value={centrosCosto.find(cc => cc.value === filters.centroCosto) || null}
                          onChange={handleCentroCostoChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    {/* Fecha de Inicio */}
                    <Col md="6">
                      <FormGroup>
                        <Label>Fecha de Inicio</Label>
                        <Row>
                          <Col md="6">
                            <Label sm="12" className="small">Inicio</Label>
                            <Flatpickr
                              className="form-control"
                              placeholder="Seleccionar fecha"
                              options={{
                                dateFormat: 'Y-m-d',
                                altInput: true,
                                altFormat: 'd/m/Y',
                              }}
                              value={filters.fechaInicio.inicio || ''}
                              onChange={(dates) => handleDateChange('fechaInicio', 'inicio', dates)}
                            />
                          </Col>
                          <Col md="6">
                            <Label sm="12" className="small">Fin</Label>
                            <Flatpickr
                              className="form-control"
                              placeholder="Seleccionar fecha"
                              options={{
                                dateFormat: 'Y-m-d',
                                altInput: true,
                                altFormat: 'd/m/Y',
                              }}
                              value={filters.fechaInicio.fin || ''}
                              onChange={(dates) => handleDateChange('fechaInicio', 'fin', dates)}
                            />
                          </Col>
                        </Row>
                        {dateErrors.fechaInicio && (
                          <div className="text-danger small mt-1">{dateErrors.fechaInicio}</div>
                        )}
                      </FormGroup>
                    </Col>

                    {/* Fecha de Fin */}
                    <Col md="6">
                      <FormGroup>
                        <Label>Fecha de Fin</Label>
                        <Row>
                          <Col md="6">
                            <Label sm="12" className="small">Inicio</Label>
                            <Flatpickr
                              className="form-control"
                              placeholder="Seleccionar fecha"
                              options={{
                                dateFormat: 'Y-m-d',
                                altInput: true,
                                altFormat: 'd/m/Y',
                              }}
                              value={filters.fechaFin.inicio || ''}
                              onChange={(dates) => handleDateChange('fechaFin', 'inicio', dates)}
                            />
                          </Col>
                          <Col md="6">
                            <Label sm="12" className="small">Fin</Label>
                            <Flatpickr
                              className="form-control"
                              placeholder="Seleccionar fecha"
                              options={{
                                dateFormat: 'Y-m-d',
                                altInput: true,
                                altFormat: 'd/m/Y',
                              }}
                              value={filters.fechaFin.fin || ''}
                              onChange={(dates) => handleDateChange('fechaFin', 'fin', dates)}
                            />
                          </Col>
                        </Row>
                        {dateErrors.fechaFin && (
                          <div className="text-danger small mt-1">{dateErrors.fechaFin}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Botón Exportar */}
                  <FormGroup>
                    <Button
                      color="primary"
                      onClick={handleExport}
                      disabled={!hasFilters || loading || Object.values(dateErrors).some(err => err !== null)}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Exportando...
                        </>
                      ) : (
                        'Exportar a Excel'
                      )}
                    </Button>
                  </FormGroup>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default InformeOperariosPage;

